import { useEffect, useState } from "react";
import { Plus, Copy, Pencil, Trash2, CheckCircle2, CreditCard, Send } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, errMsg, brl, fmtDate, CHARGE_STATUS } from "../lib/api";
import { useAthletes, useStages, copyText } from "../lib/hooks";
import { Avatar, Empty, Field, Loader, PageHeader, PillTabs, StatusPill } from "../components/common";
import { AthletePicker } from "../components/AthletePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const blank = { user_id: "", description: "", amount: "", due_date: "", status: "pendente", stage_id: "", notes: "" };

const ChargeForm = ({ open, onOpenChange, charge, onSaved }) => {
  const [athletes] = useAthletes();
  const stages = useStages();
  const [f, setF] = useState(blank);
  useEffect(() => { if (open) setF(charge ? { ...blank, ...charge, stage_id: charge.stage_id || "" } : blank); }, [open, charge]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async (e) => {
    e.preventDefault();
    if (!f.user_id) return toast.error("Selecione o atleta");
    const body = { user_id: f.user_id, description: f.description, amount: Number(f.amount || 0), due_date: f.due_date, status: f.status, stage_id: f.stage_id || null, notes: f.notes };
    try {
      if (charge) await api.put(`/charges/${charge.id}`, body); else await api.post("/charges", body);
      toast.success("Cobrança salva");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#232B3E] bg-[#121722] sm:max-w-md">
        <DialogHeader><DialogTitle className="h-display text-3xl">{charge ? "Editar cobrança" : "Nova cobrança"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Atleta"><AthletePicker testid="charge-athlete" athletes={athletes} value={f.user_id} onChange={set("user_id")} /></Field>
          <Field label="Descrição"><input required data-testid="charge-description-input" className="inp" value={f.description} onChange={set("description")} placeholder="Inscrição 2ª etapa" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Valor (R$)"><input required data-testid="charge-amount-input" type="number" step="0.01" className="inp" value={f.amount} onChange={set("amount")} /></Field>
            <Field label="Vencimento"><input data-testid="charge-due-input" type="date" className="inp" value={f.due_date} onChange={set("due_date")} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <select data-testid="charge-status-select" className="inp" value={f.status} onChange={set("status")}>
                <option value="pendente">Pendente</option><option value="aguardando">Aguardando confirmação</option><option value="pago">Pago</option>
              </select>
            </Field>
            <Field label="Etapa">
              <select data-testid="charge-stage-select" className="inp" value={f.stage_id} onChange={set("stage_id")}><option value="">Nenhuma</option>{stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
            </Field>
          </div>
          <Field label="Observações"><textarea data-testid="charge-notes-input" rows={2} className="inp" value={f.notes} onChange={set("notes")} /></Field>
          <button data-testid="charge-save-btn" className="btn-lime w-full rounded-full py-3">Salvar</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const PixCard = () => {
  const { settings } = useAuth();
  const key = settings?.pix_key || "circuitoaltax@gmail.com";
  const copy = async () => { if (await copyText(key)) toast.success("Chave PIX copiada!"); };
  return (
    <div className="card-x flex flex-wrap items-center gap-4 border-[#E4F222]/60 p-5" data-testid="pix-card">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#E4F222]/10"><CreditCard className="h-6 w-6 text-[#E4F222]" /></div>
      <div className="min-w-0 flex-1">
        <div className="eyebrow">Chave PIX (e-mail) · {settings?.pix_name}</div>
        <div data-testid="pix-key" className="truncate font-display text-2xl font-bold">{key}</div>
      </div>
      <button data-testid="pix-copy-button" onClick={copy} className="btn-lime flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Copy className="h-4 w-4" />Copiar chave</button>
    </div>
  );
};

const ChargeRow = ({ c, isAdmin, onEdit, onReload }) => {
  const markPaid = async () => {
    await api.put(`/charges/${c.id}`, { ...c, stage_id: c.stage_id || null, status: "pago" });
    toast.success("Marcada como paga"); onReload();
  };
  const del = async () => { if (!window.confirm("Excluir cobrança?")) return; await api.delete(`/charges/${c.id}`); toast.success("Cobrança excluída"); onReload(); };
  const notify = async () => {
    try { await api.post(`/charges/${c.id}/notify-paid`); toast.success("Organização avisada do pagamento"); onReload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const st = c.status_effective;
  return (
    <div data-testid={`charge-${c.id}`} className="card-x flex flex-wrap items-center gap-3 p-4 sm:flex-nowrap">
      {isAdmin && <Avatar user={c.user} size={42} />}
      <div className="min-w-0 flex-1">
        {isAdmin && <div className="truncate font-semibold">{c.user?.name || "Atleta removido"}</div>}
        <div className={`truncate text-sm ${isAdmin ? "text-[#94A3B8]" : "font-semibold"}`}>{c.description}</div>
        <div className="text-xs text-[#64748B]">Vence {fmtDate(c.due_date)}{c.notes && ` · ${c.notes}`}</div>
      </div>
      <div className="text-right">
        <div className="font-display text-xl font-bold">{brl(c.amount)}</div>
        <StatusPill testid={`charge-status-${c.id}`} status={st} label={CHARGE_STATUS[st]} />
      </div>
      <div className="flex gap-1">
        {isAdmin ? (
          <>
            {st !== "pago" && <button data-testid={`mark-paid-${c.id}`} onClick={markPaid} title="Marcar como pago" className="rounded-lg p-2 text-emerald-400 hover:bg-[#1A202E]"><CheckCircle2 className="h-4 w-4" /></button>}
            <button data-testid={`edit-charge-${c.id}`} onClick={() => onEdit(c)} className="rounded-lg p-2 text-[#94A3B8] hover:bg-[#1A202E]"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`delete-charge-${c.id}`} onClick={del} className="rounded-lg p-2 text-[#94A3B8] hover:bg-[#1A202E] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </>
        ) : (st === "pendente" || st === "atrasado") && (
          <button data-testid={`notify-paid-${c.id}`} onClick={notify} className="btn-ghost flex items-center gap-1 text-xs"><Send className="h-3.5 w-3.5" />Já paguei</button>
        )}
      </div>
    </div>
  );
};

export default function Charges() {
  const { isAdmin } = useAuth();
  const [status, setStatus] = useState("");
  const [list, setList] = useState(null);
  const [sum, setSum] = useState(null);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState({ open: false, charge: null });
  const load = () => {
    api.get("/charges", { params: { status } }).then((r) => setList(r.data));
    api.get("/charges/summary").then((r) => setSum(r.data));
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = (list || []).filter((c) => !q || `${c.user?.name} ${c.description}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-5">
      <PageHeader eyebrow="Financeiro" title="Cobranças" subtitle={isAdmin ? "Gerencie e edite as cobranças dos atletas." : "Suas cobranças e pagamento via PIX."}
        action={isAdmin && <button data-testid="new-charge-btn" onClick={() => setDlg({ open: true, charge: null })} className="btn-lime flex items-center gap-2 rounded-full px-5 py-2.5 text-sm"><Plus className="h-4 w-4" />Nova cobrança</button>} />
      {sum && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[["Recebido", brl(sum.received), "text-emerald-400"], ["Em aberto", brl(sum.open_amount), "text-[#E4F222]"], ["Pendentes", sum.pendente + (sum.aguardando || 0), ""], ["Em atraso", sum.atrasado, "text-red-400"]].map(([l, v, c]) => (
            <div key={l} className="card-x p-4"><div className="eyebrow">{l}</div><div data-testid={`charges-sum-${l.toLowerCase().replace(/\s/g, "-")}`} className={`font-display text-2xl font-bold ${c}`}>{v}</div></div>
          ))}
        </div>
      )}
      <PixCard />
      <PillTabs testid="charge-filter" tabs={[{ value: "", label: "Todas" }, { value: "pendente", label: "Pendentes" }, { value: "atrasado", label: "Em atraso" }, { value: "aguardando", label: "Aguardando" }, { value: "pago", label: "Pagas" }]} value={status} onChange={setStatus} />
      {isAdmin && <input data-testid="charge-search-input" className="inp" placeholder="Buscar por atleta ou descrição..." value={q} onChange={(e) => setQ(e.target.value)} />}
      {!list ? <Loader /> : shown.length === 0 ? <Empty icon={CreditCard} title="Nenhuma cobrança" text="Nada por aqui neste filtro." /> : (
        <div className="space-y-2">{shown.map((c) => <ChargeRow key={c.id} c={c} isAdmin={isAdmin} onEdit={(ch) => setDlg({ open: true, charge: ch })} onReload={load} />)}</div>
      )}
      {isAdmin && <ChargeForm open={dlg.open} charge={dlg.charge} onOpenChange={(o) => setDlg({ ...dlg, open: o })} onSaved={load} />}
    </div>
  );
}
